import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled, { css } from "styled-components";

export interface RadioButtonProps {
  checked: boolean;
  disabled?: boolean;
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
}

/** DesignV2 radio button for mutually exclusive choices. */
export const RadioButton = (props: RadioButtonProps): JSX.Element => {
  return (
    <Label $disabled={Boolean(props.disabled)}>
      <HiddenInput
        type="radio"
        name={props.name}
        value={props.value}
        checked={props.checked}
        disabled={props.disabled}
        onChange={() => props.onChange(props.value)}
      />
      <Control $checked={props.checked} $disabled={Boolean(props.disabled)}>
        <Dot $checked={props.checked} />
      </Control>
      <Text $disabled={Boolean(props.disabled)}>{props.label}</Text>
    </Label>
  );
};

const Label = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const Control = styled.span<{ $checked: boolean; $disabled: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${ColorV2.Blue5};
  background-color: ${ColorV2.White};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;

  ${HiddenInput}:focus-visible + & {
    box-shadow: 0 0 0 3px ${ColorV2.AbsenceLightBlue};
  }

  ${(props) =>
    props.$checked &&
    css`
      border-color: ${ColorV2.Blue1};
      background-color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$disabled &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${props.$checked ? ColorV2.Blue6 : ColorV2.Blue7};
    `}
`;

const Dot = styled.span<{ $checked: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background-color: ${ColorV2.White};
  opacity: ${(props) => (props.$checked ? 1 : 0)};
`;

const Text = styled.span<{ $disabled: boolean }>`
  ${TypographyV2.b1}
  color: ${(props) => (props.$disabled ? ColorV2.Blue5 : ColorV2.Blue1)};
`;
